import { Injectable, Renderer2, inject, DOCUMENT } from '@angular/core';
import { Observable, of } from 'rxjs';
import { isNil } from 'lodash-es';


export interface Script {
  name: string;
  src: string;
  loaded: boolean;
}

export const scriptsStore: Script[] = [
  { name: 'stripe', src: 'https://js.stripe.com/v3/', loaded: false },
];

export interface Scripts {
  [scriptName: string]: Script;
}

@Injectable({
  providedIn: 'root'
})
export class ScriptsLazyLoaderService {
  private document = inject<Document>(DOCUMENT);


  private scripts: Scripts = {};

  /**
   * Loads a script dynamically.
   * @param script - The script configuration to load
   * @param renderer2 - Angular Renderer2 instance
   * @param nonce - Optional CSP nonce for script element (for strict CSP compliance)
   */
  load(script: Script, renderer2: Renderer2, nonce?: string): Observable<Script> {
    const existingScript = this.scripts[script.name];

    if (!isNil(existingScript) && existingScript.loaded) {
      return of(existingScript);
    }

    return new Observable(observer => {
      this.scripts = {
        ...this.scripts,
        [script.name]: script,
      };

      const scriptElement = renderer2.createElement(('script'));
      scriptElement.type = 'text/javascript';
      scriptElement.src = script.src;
      scriptElement.async = true;

      // CSP compliance: Add nonce if provided
      if (nonce) {
        scriptElement.nonce = nonce;
      }

      scriptElement.onload = () => {
        this.scripts[script.name].loaded = true;

        observer.next(this.scripts[script.name]);
        observer.complete();
      };

      scriptElement.onerror = () => {
        observer.error(new Error(`Unable to load script: ${script.src}`));
      };

      renderer2.appendChild(this.document.body, scriptElement);
    });
  }
}
